import axios from 'axios';
import { $accessToken, $authLoading, clearAuth } from './stores/auth';
import { apiRefreshToken } from './api/auth';
import { ENCODED_JSON_CONTENT_TYPE, encodeJsonBody, isJsonBody } from './encoded-json';
import { isNetworkError } from './network-state';

const httpClient = axios.create({
  baseURL: '',
  withCredentials: true,
});

// Separate instance for refresh calls — avoids interceptor loop
const refreshClient = axios.create({
  baseURL: '',
  withCredentials: true,
});

// Request interceptor: attach access token
httpClient.interceptors.request.use((config) => {
  const token = $accessToken.get();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  /*
   * JSON bodies go out base64-encoded: chat saves and other writes carry
   * generated code that Render's edge firewall can mistake for an attack and
   * 403 before it reaches studio (see encoded-json). Studio decodes them back.
   * A retried request already holds the encoded string, so it isn't re-encoded.
   */
  if (isJsonBody(config.data)) {
    config.data = encodeJsonBody(config.data);
    config.headers['Content-Type'] = ENCODED_JSON_CONTENT_TYPE;
  }

  return config;
});

// Response interceptor: handle 401 with token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: any) => void;
}> = [];

function processQueue(error: any, token: string | null = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
}

httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't retry refresh endpoint itself or already-retried requests
    if (error.response?.status !== 401 || originalRequest._retry || originalRequest.url?.startsWith('/api/auth/')) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(httpClient(originalRequest));
          },
          reject,
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { accessToken: newToken } = await apiRefreshToken();
      $accessToken.set(newToken);
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      processQueue(null, newToken);

      return httpClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);

      // Only wipe session if auth loading is done (not mid-restore), and only
      // when the server said the session is gone — not when it couldn't be
      // reached (a deploy or restart), which used to sign people out.
      const serverRejected = !isNetworkError(refreshError) && !((refreshError as any)?.response?.status >= 500);

      if (!$authLoading.get() && serverRejected) {
        clearAuth();

        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export { refreshClient };
export default httpClient;
